import React, { useState } from 'react';
import { BIO } from '@/constants/testIds';

const PAYMENT_NETWORKS = [
  'Cash App',
  'Venmo',
  'PayPal',
  'Apple Pay',
  'Zelle',
];

const INITIAL_FORM_STATE = {
  visitorName: '',
  visitorEmail: '',
  visitorPhone: '',
  tipAmount: '',
  projectMessage: '',
  paymentNetwork: '',
};

// Resolve backend base URL with /api prefix (set at build time via .env).
const API_BASE = `${
  process.env.REACT_APP_BACKEND_URL || ''
}/api`.replace(/\/+$/, '');

const INITIAL_DELIVERY = { state: 'idle', message: '' };

/**
 * IntakeFormCard — "Client Onboarding Intake".
 *
 * Layout:
 *  - Always visible: Visitor Name (required), Email (optional), Phone (optional),
 *    Brief Project Message (required).
 *  - Collapsible "$$" toggle below the message:
 *      Collapsed → a single button reading "$$" + chevron arrow.
 *      Expanded  → supportive tagline + Tip Amount + Payment Network + Submit.
 *
 * On submit:
 *   1. Update local React state (instant confirmation panel render).
 *   2. Fire-and-track a POST to `/api/intake` so the backend can dispatch an
 *      email notification via Resend. UI remains responsive while we await
 *      the response; the delivery state is shown beneath the confirmation.
 *
 * On reset: clears form, collapses tip panel, hides confirmation.
 */
const IntakeFormCard = () => {
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [submission, setSubmission] = useState(null);
  const [isTipOpen, setIsTipOpen] = useState(false);
  const [delivery, setDelivery] = useState(INITIAL_DELIVERY);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const dispatchIntake = async (payload) => {
    setDelivery({ state: 'sending', message: 'Sending notification…' });
    try {
      const response = await fetch(`${API_BASE}/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_name: payload.visitorName,
          visitor_email: payload.visitorEmail || null,
          visitor_phone: payload.visitorPhone || null,
          project_message: payload.projectMessage,
          tip_amount: payload.tipAmount || null,
          payment_network: payload.paymentNetwork || null,
        }),
      });

      if (!response.ok) {
        let detail = `Request failed (${response.status})`;
        try {
          const body = await response.json();
          if (body && body.detail) detail = body.detail;
        } catch (_err) {
          /* ignore */
        }
        setDelivery({ state: 'error', message: detail });
        return;
      }

      const body = await response.json();
      setDelivery({
        state: 'sent',
        message: body.email_id
          ? `Email delivered (id: ${body.email_id}).`
          : 'Email delivered.',
      });
    } catch (err) {
      setDelivery({
        state: 'error',
        message: `Network error: ${err.message || err}`,
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = { ...form, submittedAt: new Date().toISOString() };
    setSubmission(payload);
    // eslint-disable-next-line no-console
    console.log('[Client Onboarding Intake] submission', payload);
    // Fire backend email send (non-blocking for the UI).
    dispatchIntake(payload);
  };

  const handleReset = () => {
    setForm(INITIAL_FORM_STATE);
    setSubmission(null);
    setIsTipOpen(false);
    setDelivery(INITIAL_DELIVERY);
  };

  const toggleTip = () => setIsTipOpen((prev) => !prev);

  return (
    <section
      className="lib-card lib-form-card"
      data-testid={BIO.formCard}
      aria-label="Client Onboarding Intake"
    >
      <h2 className="lib-form-title" data-testid={BIO.formTitle}>
        Client Onboarding Intake
      </h2>
      <p className="lib-form-subtitle">
        To get in touch with Brandon, leave your info below.
      </p>

      {!submission && (
        <form className="lib-form" onSubmit={handleSubmit} noValidate>
          <label className="lib-field">
            <span className="lib-field-label">Visitor Name</span>
            <input
              type="text"
              name="visitorName"
              className="lib-input"
              placeholder="e.g. Jordan Reeves"
              value={form.visitorName}
              onChange={handleChange}
              data-testid={BIO.inputVisitorName}
              required
              autoComplete="name"
            />
          </label>

          <label className="lib-field">
            <span className="lib-field-label">
              Email <span className="lib-field-optional">(optional)</span>
            </span>
            <input
              type="email"
              name="visitorEmail"
              className="lib-input"
              placeholder="you@example.com"
              value={form.visitorEmail}
              onChange={handleChange}
              data-testid={BIO.inputVisitorEmail}
              autoComplete="email"
            />
          </label>

          <label className="lib-field">
            <span className="lib-field-label">
              Phone <span className="lib-field-optional">(optional)</span>
            </span>
            <input
              type="tel"
              name="visitorPhone"
              className="lib-input"
              placeholder="(555) 123-4567"
              value={form.visitorPhone}
              onChange={handleChange}
              data-testid={BIO.inputVisitorPhone}
              autoComplete="tel"
            />
          </label>

          <label className="lib-field">
            <span className="lib-field-label">Brief Project Message</span>
            <textarea
              name="projectMessage"
              className="lib-input lib-textarea"
              placeholder="Tell us briefly what you're working on..."
              value={form.projectMessage}
              onChange={handleChange}
              data-testid={BIO.inputProjectMessage}
              rows={4}
              required
            />
          </label>

          <button
            type="button"
            className={`lib-tip-toggle${isTipOpen ? ' is-open' : ''}`}
            onClick={toggleTip}
            data-testid={BIO.tipToggle}
            aria-expanded={isTipOpen}
            aria-controls={BIO.tipPanel}
          >
            <span className="lib-tip-toggle-label">$$</span>
            <span className="lib-tip-toggle-arrow" aria-hidden="true">
              {isTipOpen ? '\u25B2' : '\u25BC'}
            </span>
          </button>

          {isTipOpen && (
            <div
              id={BIO.tipPanel}
              className="lib-tip-panel"
              data-testid={BIO.tipPanel}
            >
              <p className="lib-tip-message">
                If my work is helping you push forward, feel free to support the
                mission.
              </p>

              <label className="lib-field">
                <span className="lib-field-label">
                  Consultation / Tip Amount
                </span>
                <input
                  type="text"
                  name="tipAmount"
                  className="lib-input"
                  placeholder="e.g. $50"
                  value={form.tipAmount}
                  onChange={handleChange}
                  data-testid={BIO.inputTipAmount}
                  required
                  inputMode="decimal"
                />
              </label>

              <label className="lib-field">
                <span className="lib-field-label">Payment Network</span>
                <select
                  name="paymentNetwork"
                  className="lib-input lib-select"
                  value={form.paymentNetwork}
                  onChange={handleChange}
                  data-testid={BIO.selectPaymentNetwork}
                  required
                >
                  <option value="" disabled>
                    Select a payment network
                  </option>
                  {PAYMENT_NETWORKS.map((network) => (
                    <option key={network} value={network}>
                      {network}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="submit"
                className="lib-submit-button"
                data-testid={BIO.submitButton}
              >
                Send Tip &amp; Submit
              </button>
            </div>
          )}

          {!isTipOpen && (
            <button
              type="submit"
              className="lib-submit-button"
              data-testid={`${BIO.submitButton}-no-tip`}
            >
              Send Message
            </button>
          )}
        </form>
      )}

      {submission && (
        <div
          className="lib-confirmation"
          data-testid={BIO.confirmationPanel}
          role="status"
          aria-live="polite"
        >
          <h3 className="lib-confirmation-title">Intake received</h3>
          <p className="lib-confirmation-subtitle">
            Captured locally and dispatching an email notification.
          </p>

          {delivery.state !== 'idle' && (
            <div
              className={`lib-delivery lib-delivery--${delivery.state}`}
              data-testid="bio-delivery-status"
              data-delivery-state={delivery.state}
            >
              <span className="lib-delivery-dot" aria-hidden="true" />
              <span className="lib-delivery-text">
                {delivery.state === 'sending' && 'Sending email notification…'}
                {delivery.state === 'sent' &&
                  (delivery.message || 'Email delivered.')}
                {delivery.state === 'error' &&
                  `Email delivery failed: ${delivery.message}`}
              </span>
            </div>
          )}

          <dl className="lib-confirmation-list">
            <div className="lib-confirmation-row">
              <dt>Visitor Name</dt>
              <dd data-testid={BIO.confirmationName}>
                {submission.visitorName}
              </dd>
            </div>
            {submission.visitorEmail && (
              <div className="lib-confirmation-row">
                <dt>Email</dt>
                <dd data-testid={BIO.confirmationEmail}>
                  {submission.visitorEmail}
                </dd>
              </div>
            )}
            {submission.visitorPhone && (
              <div className="lib-confirmation-row">
                <dt>Phone</dt>
                <dd data-testid={BIO.confirmationPhone}>
                  {submission.visitorPhone}
                </dd>
              </div>
            )}
            <div className="lib-confirmation-row">
              <dt>Brief Project Message</dt>
              <dd data-testid={BIO.confirmationMessage}>
                {submission.projectMessage}
              </dd>
            </div>
            {submission.tipAmount && (
              <div className="lib-confirmation-row">
                <dt>Consultation / Tip Amount</dt>
                <dd data-testid={BIO.confirmationAmount}>
                  {submission.tipAmount}
                </dd>
              </div>
            )}
            {submission.paymentNetwork && (
              <div className="lib-confirmation-row">
                <dt>Payment Network</dt>
                <dd data-testid={BIO.confirmationNetwork}>
                  {submission.paymentNetwork}
                </dd>
              </div>
            )}
          </dl>

          <button
            type="button"
            className="lib-reset-button"
            onClick={handleReset}
            data-testid={BIO.resetButton}
          >
            Submit another intake
          </button>
        </div>
      )}
    </section>
  );
};

export default IntakeFormCard;
